import React, { createContext, useContext, useReducer, useCallback, useMemo } from 'react';
import useDashboardData from '../hooks/useDashboardData';
import useSimplePatientSearch from '../hooks/useSimplePatientSearch';

// Action types for dashboard state management
const DASHBOARD_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_USER_PREFERENCES: 'SET_USER_PREFERENCES',
  SET_NOTIFICATION_SETTINGS: 'SET_NOTIFICATION_SETTINGS',
  SET_THEME: 'SET_THEME',
  SET_LANGUAGE: 'SET_LANGUAGE',
  UPDATE_LAST_ACTIVITY: 'UPDATE_LAST_ACTIVITY'
};

// Initial state for dashboard context
const initialState = {
  // UI State
  isLoading: false,
  error: null,
  theme: 'light',
  language: 'en',
  
  // User preferences
  userPreferences: {
    dashboardLayout: 'default',
    autoRefresh: false, // Disable auto-refresh by default
    refreshInterval: 600000, // 10 minutes instead of 5
    itemsPerPage: 10,
    defaultView: 'table',
    showNotifications: true,
    compactMode: false
  },
  
  // Notification settings
  notificationSettings: {
    enabled: true,
    sound: true,
    desktop: true,
    email: false,
    types: {
      medication: true,
      appointment: true,
      system: true,
      emergency: true
    }
  },
  
  // Activity tracking
  lastActivity: Date.now()
};

// Reducer for dashboard state management
const dashboardReducer = (state, action) => {
  switch (action.type) {
    case DASHBOARD_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };
      
    case DASHBOARD_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isLoading: false
      };
      
    case DASHBOARD_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };
      
    case DASHBOARD_ACTIONS.SET_USER_PREFERENCES:
      return {
        ...state,
        userPreferences: {
          ...state.userPreferences,
          ...action.payload
        }
      };
      
    case DASHBOARD_ACTIONS.SET_NOTIFICATION_SETTINGS:
      return {
        ...state,
        notificationSettings: {
          ...state.notificationSettings,
          ...action.payload
        }
      };
      
    case DASHBOARD_ACTIONS.SET_THEME:
      return {
        ...state,
        theme: action.payload
      };
      
    case DASHBOARD_ACTIONS.SET_LANGUAGE:
      return {
        ...state,
        language: action.payload
      };
      
    case DASHBOARD_ACTIONS.UPDATE_LAST_ACTIVITY:
      return {
        ...state,
        lastActivity: Date.now()
      };
      
    default:
      return state;
  }
};

// Create Dashboard Context
const DashboardContext = createContext();

// Dashboard Provider Component
export const DashboardProvider = ({ children }) => {
  const [state, dispatch] = useReducer(dashboardReducer, initialState);
  
  // Initialize dashboard data hook with user preferences
  const dashboardData = useDashboardData({
    autoRefresh: state.userPreferences.autoRefresh,
    refreshInterval: state.userPreferences.refreshInterval,
    enableCaching: true,
    cacheExpiry: 60000
  });
  
  // Initialize simplified patient search hook
  const patientSearch = useSimplePatientSearch();
  
  // Action creators
  const actions = useMemo(() => ({
    setLoading: (loading) => {
      dispatch({ type: DASHBOARD_ACTIONS.SET_LOADING, payload: loading });
    },
    
    setError: (error) => {
      dispatch({ type: DASHBOARD_ACTIONS.SET_ERROR, payload: error });
    },
    
    clearError: () => {
      dispatch({ type: DASHBOARD_ACTIONS.CLEAR_ERROR });
    },
    
    setUserPreferences: (preferences) => {
      dispatch({ type: DASHBOARD_ACTIONS.SET_USER_PREFERENCES, payload: preferences });
      // Save to localStorage
      try {
        localStorage.setItem('dashboardPreferences', JSON.stringify({
          ...state.userPreferences,
          ...preferences
        }));
      } catch (error) {
        console.error('Failed to save user preferences:', error);
      }
    },
    
    setNotificationSettings: (settings) => {
      dispatch({ type: DASHBOARD_ACTIONS.SET_NOTIFICATION_SETTINGS, payload: settings });
      // Save to localStorage
      try {
        localStorage.setItem('notificationSettings', JSON.stringify({
          ...state.notificationSettings,
          ...settings
        }));
      } catch (error) {
        console.error('Failed to save notification settings:', error);
      }
    },
    
    setTheme: (theme) => {
      dispatch({ type: DASHBOARD_ACTIONS.SET_THEME, payload: theme });
      // Save to localStorage
      try {
        localStorage.setItem('dashboardTheme', theme);
        // Apply theme to document
        document.documentElement.setAttribute('data-theme', theme);
      } catch (error) {
        console.error('Failed to save theme:', error);
      }
    },
    
    setLanguage: (language) => {
      dispatch({ type: DASHBOARD_ACTIONS.SET_LANGUAGE, payload: language });
      // Save to localStorage
      try {
        localStorage.setItem('dashboardLanguage', language);
      } catch (error) {
        console.error('Failed to save language:', error);
      }
    },
    
    updateLastActivity: () => {
      dispatch({ type: DASHBOARD_ACTIONS.UPDATE_LAST_ACTIVITY });
    }
  }), [state.userPreferences, state.notificationSettings]);
  
  // Enhanced dashboard actions - defined as separate useCallback hooks
  const refreshDashboard = useCallback(async (forceRefresh = false) => {
    actions.setLoading(true);
    actions.clearError();
    
    try {
      const result = await dashboardData.refreshDashboard(forceRefresh);
      if (!result.success) {
        throw new Error(result.error || 'Failed to refresh dashboard');
      }
      
      // Update patient search data after successful refresh
      if (patientSearch.updateData && dashboardData.formData) {
        patientSearch.updateData(dashboardData.formData);
      }
      
      actions.updateLastActivity();
      return result;
    } catch (error) {
      actions.setError(error.message);
      throw error;
    } finally {
      actions.setLoading(false);
    }
  }, [dashboardData, actions, patientSearch.updateData]);
  
  const refreshStaffDashboard = useCallback(async (forceRefresh = false) => {
    actions.setLoading(true);
    actions.clearError();
    
    try {
      const result = await dashboardData.refreshStaffDashboard(forceRefresh);
      if (!result.success) {
        throw new Error(result.error || 'Failed to refresh staff dashboard');
      }
      
      // Update patient search data after successful refresh
      if (patientSearch.updateData && dashboardData.formData) {
        patientSearch.updateData(dashboardData.formData);
      }
      
      actions.updateLastActivity();
      return result;
    } catch (error) {
      actions.setError(error.message);
      throw error;
    } finally {
      actions.setLoading(false);
    }
  }, [dashboardData, actions, patientSearch.updateData]);
  
  const searchPatients = useCallback(async () => {
    actions.clearError();
    
    try {
      const result = patientSearch.performSearch();
      actions.updateLastActivity();
      return result;
    } catch (error) {
      actions.setError(error.message);
      throw error;
    }
  }, [patientSearch, actions]);
  
  const resetPatientSearch = useCallback(() => {
    const result = patientSearch.resetSearch();
    actions.updateLastActivity();
    return result;
  }, [patientSearch, actions]);
  
  const fetchMedicationDetails = useCallback(async (mrnNumber) => {
    actions.setLoading(true);
    actions.clearError();
    
    try {
      const result = await dashboardData.fetchMedpassPrescriptionList(mrnNumber);
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch medication details');
      }
      
      actions.updateLastActivity();
      return result;
    } catch (error) {
      actions.setError(error.message);
      throw error;
    } finally {
      actions.setLoading(false);
    }
  }, [dashboardData, actions]);

  const fetchPatientRecordDashboard = useCallback(async (fromDate, toDate) => {
    actions.setLoading(true);
    actions.clearError();
    
    try {
      const result = await dashboardData.fetchPatientRecordDashboard(fromDate, toDate);
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch patient record dashboard');
      }
      
      actions.updateLastActivity();
      return result;
    } catch (error) {
      actions.setError(error.message);
      throw error;
    } finally {
      actions.setLoading(false);
    }
  }, [dashboardData, actions]);

  // Enhanced dashboard actions object
  const dashboardActions = useMemo(() => ({
    refreshDashboard,
    refreshStaffDashboard,
    searchPatients,
    resetPatientSearch,
    fetchMedicationDetails,
    fetchPatientRecordDashboard
  }), [refreshDashboard, refreshStaffDashboard, searchPatients, resetPatientSearch, fetchMedicationDetails, fetchPatientRecordDashboard]);
  
  // Create a final, memoized actions object to ensure its reference is stable
  const finalActions = useMemo(() => ({
    ...actions,
    ...dashboardActions,
  }), [actions, dashboardActions]);

  // Create a stable interface for patientSearch to pass to consumers
  const patientSearchInterface = useMemo(() => ({
    searchTerms: patientSearch.searchTerms,
    filteredData: patientSearch.filteredData,
    isFiltered: patientSearch.isFiltered,
    searchError: patientSearch.searchError,
    searchSummary: patientSearch.searchSummary,
    handleSearchChange: patientSearch.handleSearchChange,
    performSearch: patientSearch.performSearch,
    resetSearch: patientSearch.resetSearch,
  }), [
    patientSearch.searchTerms,
    patientSearch.filteredData,
    patientSearch.isFiltered,
    patientSearch.searchError,
    patientSearch.searchSummary,
    patientSearch.handleSearchChange,
    patientSearch.performSearch,
    patientSearch.resetSearch,
  ]);

  // Memoize utility functions
  const getPatientById = useCallback((id) => {
    return dashboardData.formData?.find(patient => 
      patient.id === id || patient.recordId === id
    );
  }, [dashboardData.formData]);
  
  const getPatientByMrn = useCallback((mrnNumber) => {
    return dashboardData.formData?.find(patient => 
      patient.mrnNumber === mrnNumber
    );
  }, [dashboardData.formData]);

  // Load saved preferences on mount
  React.useEffect(() => {
    try {
      // Load user preferences and ensure autoRefresh is disabled for stability
      const savedPreferences = localStorage.getItem('dashboardPreferences');
      if (savedPreferences) {
        const preferences = JSON.parse(savedPreferences);
        // Force disable autoRefresh to prevent spam
        preferences.autoRefresh = false;
        actions.setUserPreferences(preferences);
      }
      
      // Load notification settings
      const savedNotifications = localStorage.getItem('notificationSettings');
      if (savedNotifications) {
        const notifications = JSON.parse(savedNotifications);
        actions.setNotificationSettings(notifications);
      }
      
      // Load theme
      const savedTheme = localStorage.getItem('dashboardTheme');
      if (savedTheme) {
        actions.setTheme(savedTheme);
      }
      
      // Load language
      const savedLanguage = localStorage.getItem('dashboardLanguage');
      if (savedLanguage) {
        actions.setLanguage(savedLanguage);
      }
    } catch (error) {
      console.error('Failed to load saved preferences:', error);
    }
  }, [actions]);

  // Remove useEffect to prevent infinite loops - data will be updated via actions
  
  // Context value with memoization for performance
  const contextValue = useMemo(() => ({
    // State
    ...state,
    
    // Dashboard data
    ...dashboardData,
    
    // Patient search (without updateData to prevent circular dependencies)
    patientSearch: patientSearchInterface,
    
    // Actions
    actions: finalActions,
    
    // Computed values
    isAnyLoading: state.isLoading || dashboardData.isLoading,
    hasAnyError: state.error || dashboardData.hasError,
    
    // Utility functions
    getPatientById,
    getPatientByMrn
  }), [
    state,
    dashboardData,
    patientSearchInterface,
    finalActions,
    getPatientById,
    getPatientByMrn
  ]);
  
  return (
    <DashboardContext.Provider value={contextValue}>
      {children}
    </DashboardContext.Provider>
  );
};

// Custom hook to use dashboard context
export const useDashboardContext = () => {
  const context = useContext(DashboardContext);
  
  if (!context) {
    throw new Error('useDashboardContext must be used within a DashboardProvider');
  }
  
  return context;
};

// HOC for components that need dashboard context
export const withDashboardContext = (Component) => {
  return function WrappedComponent(props) {
    return (
      <DashboardProvider>
        <Component {...props} />
      </DashboardProvider>
    );
  };
};

export default DashboardContext; 